import React, { useState, useEffect, useRef, useCallback } from 'react';
import comms from '../../comms';
import { normalizeMapLatLng } from './locationHelper';

const GPS_TIMEOUT_MS = 12000;
/**
 * Backstop for "dispatch never replied". JOBMF arrival is the canonical ack — this only
 * fires when the link is stuck or the request hit a server error that no MSG/JOBMF reports.
 */
const DISPATCH_BACKSTOP_MS = 12000;

/**
 * Dispatch sends this when FLAGM coordinates cannot be reverse-geocoded / zoned.
 * Match loosely so minor server wording changes still surface in the modal.
 */
function isFlagTripAddressFailureMessage(text) {
  if (!text || typeof text !== 'string') return false;
  const t = text.toLowerCase();
  return t.includes('derive address') && t.includes('flag trip');
}

/**
 * LiveDispatch modal: flag an unscheduled street pickup (FLAGM socket message).
 *
 * Resolution:
 *  - On Submit, send FLAGM and arm an awaiting state.
 *  - The parent registers `registerPendingFlagAck(resolveFn)` so when JOBMF arrives in
 *    LiveDispatch.onFlagTrip, it resolves the modal with the new jobNumber.
 *  - Address-failure MSG (no JOBMF) flips the modal back to error state.
 *  - DISPATCH_BACKSTOP_MS timeout shows "Dispatch did not respond" rather than auto-success.
 */
const FlagTripModal = ({ isOpen, onClose, currentLocation, onSuccess, registerPendingFlagAck }) => {
  const [passengerCount, setPassengerCount] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [awaitingDispatch, setAwaitingDispatch] = useState(false);
  const [error, setError] = useState(null);

  const lastSubmitRef = useRef(0);
  const awaitingDispatchRef = useRef(false);
  const dispatchWaitTimerRef = useRef(null);
  const SUBMIT_COOLDOWN_MS = 5000;

  const clearDispatchWait = useCallback(() => {
    awaitingDispatchRef.current = false;
    setAwaitingDispatch(false);
    comms.setFlagTripAwaitingMessageHandler(null);
    if (typeof registerPendingFlagAck === 'function') {
      try { registerPendingFlagAck(null); } catch (_) { /* ignore */ }
    }
    if (dispatchWaitTimerRef.current) {
      clearTimeout(dispatchWaitTimerRef.current);
      dispatchWaitTimerRef.current = null;
    }
  }, [registerPendingFlagAck]);

  const finishAwaitSuccess = useCallback(
    (jobNumber) => {
      clearDispatchWait();
      if (onSuccess) onSuccess(jobNumber);
      if (onClose) onClose();
    },
    [clearDispatchWait, onSuccess, onClose]
  );

  useEffect(() => {
    if (isOpen) {
      setPassengerCount(1);
      setError(null);
      setIsSubmitting(false);
      setAwaitingDispatch(false);
      awaitingDispatchRef.current = false;
      comms.setFlagTripAwaitingMessageHandler(null);
      if (dispatchWaitTimerRef.current) {
        clearTimeout(dispatchWaitTimerRef.current);
        dispatchWaitTimerRef.current = null;
      }
    }
  }, [isOpen]);

  useEffect(() => {
    return () => clearDispatchWait();
  }, [clearDispatchWait]);

  const handlePassengerChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= 1) {
      setPassengerCount(value);
    } else if (e.target.value === '') {
      setPassengerCount('');
    }
  };

  const incrementPassengers = () => {
    setPassengerCount((prev) => (parseInt(prev, 10) || 0) + 1);
  };

  const decrementPassengers = () => {
    setPassengerCount((prev) => Math.max(1, (parseInt(prev, 10) || 1) - 1));
  };

  const handleClose = () => {
    clearDispatchWait();
    if (onClose) onClose();
  };

  const handleSubmit = async () => {
    const count = parseInt(passengerCount, 10);
    if (!count || count < 1) {
      setError('Please enter at least 1 passenger');
      return;
    }

    const startCoords = normalizeMapLatLng(currentLocation);
    if (!startCoords) {
      setError('GPS location not available. Please wait for GPS signal.');
      return;
    }

    const now = Date.now();
    if (now - lastSubmitRef.current < SUBMIT_COOLDOWN_MS) {
      setError('Please wait a moment before submitting again.');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    lastSubmitRef.current = now;

    try {
      let lat = startCoords.lat;
      let lng = startCoords.lng;

      try {
        const freshPosition = await new Promise((resolve, reject) => {
          const timeoutId = setTimeout(() => reject(new Error('GPS timeout')), GPS_TIMEOUT_MS);
          navigator.geolocation.getCurrentPosition(
            (position) => {
              clearTimeout(timeoutId);
              resolve(position);
            },
            (gpsErr) => {
              clearTimeout(timeoutId);
              reject(gpsErr);
            },
            { enableHighAccuracy: true, timeout: GPS_TIMEOUT_MS, maximumAge: 60000 }
          );
        });
        lat = freshPosition.coords.latitude;
        lng = freshPosition.coords.longitude;
      } catch (gpsErr) {
        console.warn('FlagTripModal: Fresh GPS unavailable, using last known position:', gpsErr?.message);
      }

      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        lat = startCoords.lat;
        lng = startCoords.lng;
      }

      // Arm the awaiting state and register the JOBMF resolver BEFORE sending FLAGM.
      // Dispatch can return JOBMF before the sendFlagTrip promise settles; if we
      // register the resolver after the await, the JOBMF arrives with a null
      // resolver and the modal hangs until the backstop timer fires.
      awaitingDispatchRef.current = true;
      setAwaitingDispatch(true);

      if (typeof registerPendingFlagAck === 'function') {
        try {
          registerPendingFlagAck((jobNumber) => {
            if (!awaitingDispatchRef.current) return;
            finishAwaitSuccess(jobNumber);
          });
        } catch (err) {
          console.warn('FlagTripModal: registerPendingFlagAck failed', err);
        }
      }

      // Listen for address-failure MSG (server can't reverse-geocode the FLAGM coords).
      comms.setFlagTripAwaitingMessageHandler((text) => {
        if (!awaitingDispatchRef.current) return;
        if (isFlagTripAddressFailureMessage(text)) {
          clearDispatchWait();
          setError(text.trim());
        }
      });

      dispatchWaitTimerRef.current = setTimeout(() => {
        if (!awaitingDispatchRef.current) return;
        clearDispatchWait();
        setError('Dispatch did not respond. Please try again.');
      }, DISPATCH_BACKSTOP_MS);

      const result = await comms.sendFlagTrip({ lat, lng }, count);
      if (result && result.sent === false) {
        clearDispatchWait();
        setError('Not connected to dispatch. Check your connection and try again.');
        setIsSubmitting(false);
        return;
      }

      setIsSubmitting(false);
    } catch (err) {
      console.error('FlagTripModal: submit error', err);
      setError('Failed to send flag trip. Please try again.');
      setIsSubmitting(false);
      clearDispatchWait();
    }
  };

  if (!isOpen) return null;

  const loc = normalizeMapLatLng(currentLocation);
  const busy = isSubmitting || awaitingDispatch;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden m-4">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-orange-500">
          <h2 className="text-xl font-semibold text-white">Flag Trip</h2>
          <button
            type="button"
            onClick={handleClose}
            className="text-white hover:text-gray-200 transition-colors"
            disabled={isSubmitting}
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              {error}
            </div>
          )}

          {awaitingDispatch && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded text-blue-800 text-sm flex items-center gap-2">
              <svg
                className="animate-spin h-5 w-5 flex-shrink-0"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span>Sent to dispatch. Waiting for trip details…</span>
            </div>
          )}

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
              Passenger count
            </label>
            <div className="flex items-center justify-center gap-4">
              <button
                type="button"
                onClick={decrementPassengers}
                className="w-14 h-14 rounded-full bg-orange-500 hover:bg-orange-600 flex items-center justify-center text-2xl font-bold text-white transition-colors"
                disabled={busy || passengerCount <= 1}
              >
                -
              </button>
              <input
                type="number"
                min={1}
                value={passengerCount}
                onChange={handlePassengerChange}
                className="w-24 h-16 text-center text-3xl font-bold border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                disabled={busy}
              />
              <button
                type="button"
                onClick={incrementPassengers}
                className="w-14 h-14 rounded-full bg-orange-500 hover:bg-orange-600 flex items-center justify-center text-2xl font-bold text-white transition-colors"
                disabled={busy}
              >
                +
              </button>
            </div>
          </div>

          <div className="mb-2 text-center text-sm text-gray-500">
            {loc ? (
              <span className="text-green-600">GPS ready</span>
            ) : (
              <span className="text-orange-500">Waiting for GPS…</span>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={
              busy ||
              !passengerCount ||
              parseInt(passengerCount, 10) < 1 ||
              currentLocation?.lat == null ||
              currentLocation?.lng == null
            }
            className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <svg
                  className="animate-spin h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Submitting…
              </>
            ) : awaitingDispatch ? (
              <>
                <svg
                  className="animate-spin h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Waiting…
              </>
            ) : (
              'Submit'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FlagTripModal;
